"use client"
import { Box, Text } from "@chakra-ui/react"
import { Link } from "react-router-dom"
import { motion, useScroll, useTransform } from "framer-motion"
import { ArrowRightIcon } from "@heroicons/react/24/outline"
import HomeSignup from "../../../features/auth/components/HomeSignup"

const MotionBox = motion(Box)
const MotionText = motion(Text)

export default function HeroSection() {
    const { scrollYProgress } = useScroll()
    const heroY = useTransform(scrollYProgress, [0, 0.5], [0, -100])
    const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.5])

    return (
        <Box
            as="section"
            position="relative"
            className="h-auto md:h-screen lg:h-screen min-h-0 md:min-h-[700px] overflow-hidden pt-16 md:pt-0" // Added pt-16 for mobile
        >
            {/* Background gradient overlay - Chakra UI */}
            <Box position="absolute" top="0" left="0" right="0" bottom="0" className="bg-gradient-primary" zIndex="1" />

            {/* Background pattern - Chakra UI */}
            <MotionBox
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                opacity="0.1"
                backgroundImage="url('https://www.transparenttextures.com/patterns/cubes.png')"
                zIndex="2"
                style={{ y: heroY }}
                className="animate-fadeIn"
            />

            {/* Animated particles - Chakra UI */}
            <Box position="absolute" top="0" left="0" right="0" bottom="0" zIndex="2" overflow="hidden" opacity="0.4">
                {[...Array(20)].map((_, i) => (
                    <MotionBox
                        key={i}
                        position="absolute"
                        width={`${Math.random() * 10 + 5}px`}
                        height={`${Math.random() * 10 + 5}px`}
                        borderRadius="full"
                        bg="white"
                        left={`${Math.random() * 100}%`}
                        top={`${Math.random() * 100}%`}
                        initial={{ opacity: 0.3 }}
                        animate={{
                            y: [0, Math.random() * 100 - 50],
                            opacity: [0.3, 0.8, 0.3],
                            scale: [1, Math.random() * 0.5 + 1, 1],
                        }}
                        transition={{
                            duration: Math.random() * 10 + 10,
                            repeat: Number.POSITIVE_INFINITY,
                            ease: "easeInOut",
                        }}
                        className="animate-float"
                    />
                ))}
            </Box>

            {/* Decorative shapes - Chakra UI */}
            <Box
                position="absolute"
                top="20%"
                right="-5%"
                width={{ base: "150px", md: "300px" }}
                height={{ base: "150px", md: "300px" }}
                zIndex="1"
                opacity="0.1"
                bg="purple.500"
                className="shape-blob animate-float"
            />
            <Box
                position="absolute"
                bottom="10%"
                left="-10%"
                width={{ base: "200px", md: "400px" }}
                height={{ base: "200px", md: "400px" }}
                zIndex="1"
                opacity="0.1"
                bg="indigo.500"
                className="shape-blob-2 animate-float delay-500"
            />

            <MotionBox
                className="relative z-30 w-full px-6 sm:px-8 md:px-12 lg:px-16 xl:px-20 py-8 md:py-12 lg:py-16"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                <div className="flex flex-col lg:flex-row items-center justify-between gap-8 md:gap-12 lg:gap-0">
                    {/* Left Side - Tagline and CTA */}
                    <MotionBox
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="w-full lg:w-1/2 mb-8 lg:mb-0 mt-8 md:mt-0" // Added mt-8 for mobile
                        style={{ opacity: heroOpacity }}
                    >
                        <div className="flex flex-col items-start space-y-4 md:space-y-6">
                            <MotionBox
                                className="bg-purple-100 text-purple-800 text-sm px-3 py-1 rounded-full font-medium"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                #1 Hiring Platform for Employers
                            </MotionBox>

                            {/* Rest of your content remains the same */}
                            <MotionBox
                                as="h1"
                                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                            >
                                Empower Your Business with the{" "}
                                <Text as="span" className="text-cyan-200 relative inline-block">
                                    Right Talent
                                    <MotionBox
                                        className="absolute -bottom-1 left-0 right-0 h-1 bg-cyan-200 rounded-full"
                                        animate={{
                                            width: ["0%", "100%"],
                                        }}
                                        transition={{ duration: 1, delay: 1 }}
                                    />
                                </Text>{" "}
                            </MotionBox>

                            <MotionText
                                className="text-base sm:text-lg md:text-xl text-blue-100"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                            >
                                Connect with top professionals matched to your needs through our smart hiring algorithm.
                            </MotionText>

                            <div className="flex flex-wrap gap-4">
                                <Link to={"/login"}>
                                    <MotionBox
                                        className="flex items-center justify-center bg-white text-indigo-600 px-8 py-3 rounded-md font-bold hover:bg-blue-50 hover:shadow-lg transition-all duration-300 text-base sm:text-lg"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.5 }}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        Post a Job
                                        <ArrowRightIcon className="w-5 h-5 ml-2" />
                                    </MotionBox>
                                </Link>

                                <Link to={"https://indiahirings.com/about-us"}>
                                    <MotionBox
                                        className="flex items-center justify-center text-white px-8 py-3 rounded-md font-bold border border-white/30 hover:bg-white/20 transition-all duration-300 text-base sm:text-lg"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.6 }}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        Learn More
                                    </MotionBox>
                                </Link>
                            </div>

                            <div className="flex items-center pt-4">
                                <MotionBox
                                    className="flex items-center"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.8 }}
                                >
                                    <div className="flex relative">
                                        {[...Array(3)].map((_, i) => (
                                            <Box
                                                key={i}
                                                className={`relative -ml-${i * 2.5} z-${3 - i} border-2 border-white rounded-full overflow-hidden w-7 sm:w-9 h-7 sm:h-9`}
                                            >
                                                <img
                                                    src={`https://randomuser.me/api/portraits/men/${i + 20}.jpg`}
                                                    alt={`User ${i}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </Box>
                                        ))}
                                    </div>
                                    <Text className="text-blue-100 text-xs sm:text-sm ml-2">
                                        Trusted by <b>Thousands of</b> employers
                                    </Text>
                                </MotionBox>
                            </div>
                        </div>
                    </MotionBox>

                    {/* Right Side - Enquiry Form */}
                    <MotionBox
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="w-full lg:w-[45%] max-w-full sm:max-w-[550px] md:max-w-[550px] lg:max-w-[600px] mx-auto lg:mx-0 mt-10 lg:mt-5"
                    >
                        <div className="bg-white rounded-lg shadow-xl overflow-hidden w-full">
                            <HomeSignup />
                        </div>
                    </MotionBox>
                </div>
            </MotionBox>
        </Box>
    )
}